/**
 * Spacing Design Tokens
 *
 * Tailwind-default-aligned 4px base scale, expressed in px so spacing stays
 * absolute under the responsive root font-size in libs/styles (media_queries.scss).
 * Key N renders N * 4px. Stock Tailwind v3 key set plus `18` (72px), which stock
 * Tailwind omits but our UI uses.
 */

// Base spacing unit
export const baseSpacingUnit = 4

// Spacing scale (4px base)
// Usage: p-1 = 4px, p-2 = 8px, p-4 = 16px
export const spacing = Object.freeze({
  0: '0px',
  0.25: '1px',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  3.5: '14px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
  11: '44px',
  12: '48px',
  // The game table's xlarge seat plate (52px) — the one 4px step Tailwind's default scale skips.
  13: '52px',
  14: '56px',
  16: '64px',
  18: '72px',
  20: '80px',
  24: '96px',
  28: '112px',
  32: '128px',
  36: '144px',
  40: '160px',
  44: '176px',
  48: '192px',
  52: '208px',
  56: '224px',
  60: '240px',
  64: '256px',
  72: '288px',
  80: '320px',
  96: '384px',
  // Semantic layout tokens — outside panels → gutter, inside panels → panel.
  // Plain px on purpose: one source of truth, works in every preset consumer
  // (no dependency on libs/styles loading), and must not scale with the
  // responsive root font-size. If density ever needs to vary (per media query
  // or at runtime), reintroduce var-backing then — not before.
  // Living in the spacing scale they also generate w-gutter/h-panel/etc. —
  // accepted trade-off; only gap-*/p-*/m-* readings are sanctioned.
  gutter: '16px',
  panel: '16px',
  'custom-solutions-intro-dialog': '820px',
  'custom-solutions-intro-slide': '458px',
  // Desktop cap for the tournament-state card (Players Left slider + the avg-stack grid).
  // The Players Left tick strip sets this value, not this file: read the comment on
  // PLAYERS_LEFT_MAX_TICKS in tournament-state-section.tsx before changing it.
  'tournament-state-card': '720px',
  'tier-compare-table-min-width': '720px',
  'tier-compare-table-min-width-wide': '860px',
  // 480px = 120 * 4, above the scale's top key (96), so no key expresses it.
  'confirm-dialog': '480px',
  // The width the authored block layout is designed against. `modal-width.ts` in
  // @gto/announcement-ui reads these four back to scale the admin preview stage,
  // so the preview and the customer modal always lay a modal out the same way.
  // The operator picks one per announcement. `announcement-modal` stays the value
  // every stored modal already renders at, so widening the set changes no
  // published announcement.
  'announcement-modal-small': '560px',
  'announcement-modal': '800px',
  'announcement-modal-large': '1040px',
  'announcement-modal-xlarge': '1240px',
  // Shared announcement surfaces use these exact authored widths in customer
  // rendering and in the admin's full-screen preview.
  //
  // The dashboard slide is the exception: only the admin preview reads its token.
  // The customer slide takes its width from the dashboard column instead, so a
  // change here moves the preview alone. 1025px is the width that column really
  // gives the slide. `dashboard-route.tsx` sizes the column
  // `clamp(12rem, 75.5rem, 100vw)`, and `libs/styles/src/media_queries.scss` sets
  // the root font size to 14px up to a 1920px viewport. So the column is 1057px,
  // and the two 16px gutters leave 1025px. Above a 1920px viewport the root font
  // size is 16px and the slide gets 1176px, so this value is the usual desktop,
  // not every desktop.
  //
  // Stage the preview wider than this and it crops the artwork at a point the
  // dashboard never crops it. That drift is what the preview exists to prevent.
  'announcement-dashboard-slide': '1025px',
  'announcement-toaster-card': '369px',
})
